import React, { useEffect, useRef, useState } from 'react';
import { getUserInfo } from '../utils/tokenManager';
import { getSupplierPlacesByGoogle } from '../services/apiService';

function useGoogleMaps(apiKey) {
  const [loaded, setLoaded] = useState(!!(window.google && window.google.maps && window.google.maps.places));
  useEffect(() => {
    if (loaded) return;
    if (!apiKey) return;
    const existing = document.querySelector('script[data-gmaps-loader="true"]');
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.gmapsLoader = 'true';
    script.onload = () => setLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps JS API');
    document.head.appendChild(script);
  }, [apiKey, loaded]);
  return loaded;
}

const PlacePickerModal = ({ open, onClose, onPick, googleApiKey }) => {
  const mapsReady = useGoogleMaps(googleApiKey);
  const inputRef = useRef(null);
  const autoRef = useRef(null);
  const [selectedPlace, setSelectedPlace] = useState(null); // { name, place_id, lat, lng }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [queryText, setQueryText] = useState('');
  // Infinite scroll state
  const listRef = useRef(null);
  const PAGE_SIZE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!open) return;
    setSelectedPlace(null);
    setResults([]);
    setError('');
    setVisibleCount(PAGE_SIZE);
  }, [open]);

  useEffect(() => {
    if (!open || !mapsReady || !inputRef.current || autoRef.current) return;
    try {
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ['place_id', 'name', 'geometry'],
        types: ['establishment'],
      });
      ac.addListener('place_changed', () => {
        const p = ac.getPlace();
        if (!p) return;
        const latFn = p?.geometry?.location?.lat;
        const lngFn = p?.geometry?.location?.lng;
        const latVal = typeof latFn === 'function' ? latFn() : latFn;
        const lngVal = typeof lngFn === 'function' ? lngFn() : lngFn;
        const sel = { name: p?.name || '', place_id: p?.place_id || '', lat: latVal, lng: lngVal };
        setSelectedPlace(sel);
        setTimeout(() => { try { handleQuery(sel); } catch (e) {} }, 0);
      });
      autoRef.current = ac;
    } catch (e) {
      console.error(e);
    }
  }, [open, mapsReady]);

  const handleInputKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    if (!mapsReady) return;
    e.preventDefault();
    try {
      const google = window.google;
      if (!google || !inputRef.current) return;
      const inputVal = inputRef.current.value || '';
      if (!inputVal.trim()) return;
      const acService = new google.maps.places.AutocompleteService();
      const sessionToken = new google.maps.places.AutocompleteSessionToken();
      const predictions = await new Promise((resolve) => {
        acService.getPlacePredictions({ input: inputVal, types: ['establishment'], sessionToken }, (preds) => resolve(Array.isArray(preds) ? preds : []));
      });
      if (!predictions.length) return;
      const top = predictions[0];
      const dummy = document.createElement('div');
      const placeService = new google.maps.places.PlacesService(dummy);
      const details = await new Promise((resolve, reject) => {
        placeService.getDetails({ placeId: top.place_id, fields: ['place_id', 'name', 'geometry'] }, (res, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) resolve(res); else reject(new Error('getDetails failed: ' + status));
        });
      });
      const latFn = details?.geometry?.location?.lat;
      const lngFn = details?.geometry?.location?.lng;
      const sel = { name: details?.name || '', place_id: details?.place_id || '', lat: typeof latFn === 'function' ? latFn() : latFn, lng: typeof lngFn === 'function' ? lngFn() : lngFn };
      setSelectedPlace(sel);
      setQueryText(sel.name || inputVal);
      if (inputRef.current) inputRef.current.value = sel.name || inputVal;
      setTimeout(() => { try { handleQuery(sel); } catch {} }, 0);
    } catch (err) {
      console.error('엔터로 자동완성 선택 실패', err);
    }
  };

  const handleQuery = async (overridePlace) => {
    const target = overridePlace || selectedPlace;
    if (!target) { setError('구글 장소를 먼저 선택해 주세요.'); return; }
    try {
      setLoading(true);
      setError('');
      const { companyNo } = getUserInfo() || {};
      const ds = await getSupplierPlacesByGoogle({
        supplierNo: companyNo,
        googleKey: target.place_id,
        placeName: target.name,
        latitude: target.lat,
        longitude: target.lng,
        keyword: target.name,
      });
      const list = Array.isArray(ds?.table) ? ds.table : (Array.isArray(ds?.table1) ? ds.table1 : []);
      setResults(list);
      // Reset visible window for new result set
      setVisibleCount(PAGE_SIZE);
      if (overridePlace?.name) setQueryText(overridePlace.name);
    } catch (e) {
      console.error(e);
      setError('장소 목록을 불러오는 데 실패했습니다.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQueryText('');
    setSelectedPlace(null);
    setResults([]);
    setError('');
    if (inputRef.current) { inputRef.current.value = ''; inputRef.current.focus(); }
  };

  const handlePick = (row) => {
    if (!row) return;
    const placeNo = Number(row?.장소번호 || row?.PlaceNo || row?.placeNo || 0);
    const placeName = String(row?.장소한글명 || row?.장소명 || row?.PlaceName || row?.Description || selectedPlace?.name || '');
    if (onPick) onPick({ placeNo, placeName });
    if (onClose) onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden border border-gray-100">
        <div className="px-5 py-4 border-b flex items-center justify-between bg-white/90">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold">장소 선택</h4>
            <span className="text-xs text-gray-500">호텔 상품 등록용 장소를 검색하세요</span>
          </div>
          <button aria-label="닫기" className="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M10 8.586 4.293 2.879 2.879 4.293 8.586 10l-5.707 5.707 1.414 1.414L10 11.414l5.707 5.707 1.414-1.414L11.414 10l5.707-5.707-1.414-1.414L10 8.586Z" clipRule="evenodd" /></svg>
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-hidden">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">장소 검색</label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M12.9 14.32a8 8 0 1 1 1.414-1.414l3.387 3.386-1.414 1.415-3.387-3.387ZM14 8a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" clipRule="evenodd" /></svg>
              </span>
              <input ref={inputRef} type="text" className="w-full border rounded-lg pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50" placeholder={mapsReady ? '장소명을 입력하세요' : 'Google 로딩 중...'} disabled={!mapsReady} defaultValue={queryText} onChange={(e) => setQueryText(e.target.value)} onKeyDown={handleInputKeyDown} />
              {queryText && (
                <button type="button" onClick={handleClear} className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600" aria-label="입력 초기화">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M10 8.586 6.293 4.879 4.879 6.293 8.586 10l-3.707 3.707 1.414 1.414L10 11.414l3.707 3.707 1.414-1.414L11.414 10l3.707-5.707-1.414-1.414L10 8.586Z" clipRule="evenodd" /></svg>
                </button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-gray-500">구글 자동완성에서 장소를 선택하면 결과가 자동 조회됩니다.</p>
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-[12px]">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mt-[2px]"><path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm-.75-11.5h1.5v5h-1.5v-5Zm0 6.5h1.5v1.5h-1.5V13Z" clipRule="evenodd" /></svg>
              <span>{error}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-gray-600">검색 결과</span>
            <span className="text-[11px] bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{Array.isArray(results) ? results.length : 0}건</span>
          </div>
          <div
            className="border rounded-lg max-h-[46vh] overflow-auto relative"
            ref={listRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              const threshold = 80; // px from bottom
              const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
              if (nearBottom) {
                setVisibleCount((prev) => {
                  if (!Array.isArray(results)) return prev;
                  if (prev >= results.length) return prev;
                  const next = Math.min(prev + PAGE_SIZE, results.length);
                  return next;
                });
              }
            }}
          >
            {loading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                <div className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                  불러오는 중...
                </div>
              </div>
            )}
            <table className="w-full text-[12px] table-fixed">
              <colgroup>
                <col className="w-[58%]" />
                <col className="w-[16%]" />
                <col className="w-[14%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead className="bg-gray-50 sticky top-0 z-[1]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">장소명</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">도시</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">국가</th>
                  <th className="px-3 py-2 text-gray-700">선택</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(results) && results.length > 0 ? (
                  results.slice(0, visibleCount).map((row, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5">
                          <div className="font-medium text-gray-800 truncate" title={String(row?.장소한글명 || row?.장소명 || row?.PlaceName || row?.Description || '-')}>{row?.장소한글명 || row?.장소명 || row?.PlaceName || row?.Description || '-'}</div>
                          {(() => { const note = row?.카피라이트문구; return (typeof note === 'string' && note.trim()) ? (<div className="text-[11px] text-gray-500 truncate" title={note}>© {note}</div>) : null; })()}
                        </div>
                      </td>
                      <td className="px-3 py-2"><div className="truncate whitespace-nowrap" title={row?.도시 || row?.CityName || row?.city || ''}>{row?.도시 || row?.CityName || row?.city || ''}</div></td>
                      <td className="px-3 py-2"><div className="truncate whitespace-nowrap" title={row?.국가 || row?.CountryName || row?.country || ''}>{row?.국가 || row?.CountryName || row?.country || ''}</div></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button type="button" className="px-2.5 py-1.5 rounded-md border text-xs bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={() => handlePick(row)}>선택</button>
                      </td>
                    </tr>
                  ))
                ) : (
                  !loading && (
                    <tr>
                      <td colSpan={4} className="px-3 py-10 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-6 h-6 text-gray-400"><circle cx="11" cy="11" r="7" strokeWidth="2" /><path d="M20 20l-3.5-3.5" strokeWidth="2" /></svg>
                          <div>결과가 없습니다. 신규 장소는 TRP에서 추가 해주세요</div>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            {Array.isArray(results) && visibleCount < results.length && (
              <div className="px-3 py-2 text-center text-[12px] text-gray-500">아래로 스크롤하여 더 보기...</div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default PlacePickerModal;
